import { useIntl } from 'react-intl';
import { useCallback, useContext, useRef, useState } from 'react';

import PendingTransitFilter from '../PendingTransit/PendingTransitFilter/PendingTransitFilter';
import {
  PENDING_ON_HUB_COLUMNS,
  PENDING_ON_FLEET_COLUMNS,
  PENDING_ON_AUDIT_HUB_COLUMNS,
  PENDING_ON_AUDIT_FLEET_COLUMNS,
  PENDING_ON_UNKNOWN_COLUMNS,
  CONFIRMED_LOST_COLUMNS
} from './columns';
import {
  formatPendingOnHubData,
  formatPendingOnFleetData,
  formatPendingOnAuditHubData,
  formatPendingOnAuditFleetData,
  formatPendingOnUnknownData,
  formatConfirmedLostOrdersData,
  getInitialQuickFilter
} from './utils';
import { OpsControl } from 'contexts/ops-control.context';
import { ACL_MATRIX } from 'common/permissions';
import aclMatrix from 'common/aclMatrix';
import { WRONG_ZONE_PAGE_OPTIONS } from 'constants/ops-wrong-zone';
import {
  cleanEmptyString,
  getPaginationCount,
  isUserAuthorized
} from 'utils/helpers';
import {
  LOST_ORDERS_DATE_FORMAT,
  LOST_ORDERS_DATE_RANGE_OPTIONS,
  LOST_ORDERS_QUICK_FILTERS
} from 'constants/lost-or-damaged';
import {
  exportLostOrders,
  getLostOrdersPendingOnFleet,
  getLostOrdersPendingOnHub,
  lostOrdersPendingOnHubCount,
  lostOrdersPendingOnFleetCount,
  getLostOrdersPendingOnAudit,
  lostOrdersPendingOnAuditCount,
  getLostOrdersPendingOnUnknown,
  lostOrdersPendingOnUnknownCount,
  getConfirmedLostOrders,
  confirmedLostOrdersCount
} from 'services/ops-control';
import { getCurrentUserHubInfo } from 'utils/hubs';
import { splitSearchInput } from 'utils';

import { notify } from 'components/Notify/Notify';
import BRTable from 'components/BRTable/BRTable';
import BRButton from 'components/BRButton/BRButton';
import OpsRangeDatePicker from 'components/OpsControl/FilterComponents/RangeDatePicker';

import { ReactComponent as DeliveriesIcon } from 'assets/bosta-icons/Orders.svg';
import { ReactComponent as ClosedEye } from 'assets/bosta-icons/Eye-1.svg';

import './LostOrders.less';

const LostOrders = () => {
  const intl = useIntl();
  const { allHubs } = useContext(OpsControl);

  const [loading, setLoading] = useState(false);
  const [delieveriesCounts, setDelieveriesCounts] = useState();
  const [isDeliveriesCountLoading, setIsDeliveriesCountLoading] =
    useState(false);
  const [showDeliveriesCount, setShowDeliveriesCount] = useState(false);
  const [quickFilterValue, setQuickFilterValue] = useState(
    getInitialQuickFilter()
  );
  const [activeTab, setActiveTab] = useState('hubs');
  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);

  const canViewAllHubs = isUserAuthorized(aclMatrix.HUBS, [
    ACL_MATRIX.HUBS_ALLOWED_TO_MARK_DELIVERY_AS_LOST_IN_ANY_HUB
  ]);

  const prefillWarehouseId = () => {
    if (!canViewAllHubs) {
      const userWareHouse = getCurrentUserHubInfo()?._id;
      if (userWareHouse) return userWareHouse;
    }
  };

  const [searchOptions, setSearchOptions] = useState({
    trackingNumbers: undefined,
    warehouseId: prefillWarehouseId()
  });

  const refreshTableRef = useRef(() => {});

  // pending on hub = 0
  // pending on fleet = 1
  // pending on audit = 2
  // pending on unknown = 3
  // confirmed lost = 4
  const functions = [
    {
      fetchLostOrders: getLostOrdersPendingOnHub,
      fetchCount: lostOrdersPendingOnHubCount,
      formatData: formatPendingOnHubData,
      exportValue: 'Hub'
    },
    {
      fetchLostOrders: getLostOrdersPendingOnFleet,
      fetchCount: lostOrdersPendingOnFleetCount,
      formatData: formatPendingOnFleetData,
      exportValue: 'Star'
    },
    {
      fetchLostOrders: getLostOrdersPendingOnAudit,
      fetchCount: lostOrdersPendingOnAuditCount,
      formatData:
        activeTab === 'hubs'
          ? formatPendingOnAuditHubData
          : formatPendingOnAuditFleetData,
      exportValue: 'Audit'
    },
    {
      fetchLostOrders: getLostOrdersPendingOnUnknown,
      fetchCount: lostOrdersPendingOnUnknownCount,
      formatData: formatPendingOnUnknownData,
      exportValue: 'Unknown'
    },
    {
      fetchLostOrders: getConfirmedLostOrders,
      fetchCount: confirmedLostOrdersCount,
      formatData: formatConfirmedLostOrdersData,
      exportValue: 'ConfirmedLost'
    }
  ];

  const updateSearchOption = useCallback((option) => {
    return (value) => {
      let updatedSearchOptions;

      setSearchOptions((curr) => {
        updatedSearchOptions = {
          ...curr,
          [option]: value
        };
        return updatedSearchOptions;
      });
    };
  }, []);

  const TABLE_COLUMNS = [
    PENDING_ON_HUB_COLUMNS,
    PENDING_ON_FLEET_COLUMNS,
    ...(activeTab === 'hubs'
      ? [PENDING_ON_AUDIT_HUB_COLUMNS]
      : [PENDING_ON_AUDIT_FLEET_COLUMNS]),
    PENDING_ON_UNKNOWN_COLUMNS,
    CONFIRMED_LOST_COLUMNS
  ];

  const handleHubChange = (hubValue) => {
    const updatedSearchOptions = updateSearchOption('warehouseId')(hubValue);
    refreshTableRef.current({
      filterValues: {
        ...updatedSearchOptions,
        warehouseId: hubValue
      }
    });
  };

  const exportFunction = async () => {
    try {
      setLoading(true);
      const payload = {
        warehouseId: searchOptions.warehouseId,
        trackingNumbers: searchOptions.trackingNumbers,
        exportType: functions[quickFilterValue].exportValue,
        ...(selectedDateRange[LOST_ORDERS_DATE_RANGE_OPTIONS.START_DATE] && {
          startDate: selectedDateRange[
            LOST_ORDERS_DATE_RANGE_OPTIONS.START_DATE
          ].format(LOST_ORDERS_DATE_FORMAT)
        }),
        ...(selectedDateRange[LOST_ORDERS_DATE_RANGE_OPTIONS.END_DATE] && {
          endDate: selectedDateRange[
            LOST_ORDERS_DATE_RANGE_OPTIONS.END_DATE
          ].format(LOST_ORDERS_DATE_FORMAT)
        })
      };
      const res = await exportLostOrders(payload);
      notify(res.message, 'success');
    } catch (error) {
      notify(error.message);
    } finally {
      setLoading(false);
    }
  };

  const listFunction = async ({
    page,
    limit,
    filterValues,
    searchInputText
  }) => {
    const updatedSearchOptions = {
      ...searchOptions,
      ...filterValues,
      trackingNumbers: splitSearchInput(searchInputText)
    };

    const { fetchLostOrders, formatData } = functions[quickFilterValue];

    setShowDeliveriesCount(false);
    setLoading(true);
    try {
      const { data } = await fetchLostOrders(
        cleanEmptyString({
          limit,
          skip: (page - 1) * limit,
          ...updatedSearchOptions,
          ...(quickFilterValue === '2' && {
            pendingOn: activeTab === 'hubs' ? 'HUB' : 'STAR'
          }),
          ...(selectedDateRange[LOST_ORDERS_DATE_RANGE_OPTIONS.START_DATE] && {
            startDate: selectedDateRange[
              LOST_ORDERS_DATE_RANGE_OPTIONS.START_DATE
            ].format(LOST_ORDERS_DATE_FORMAT)
          }),
          ...(selectedDateRange[LOST_ORDERS_DATE_RANGE_OPTIONS.END_DATE] && {
            endDate: selectedDateRange[
              LOST_ORDERS_DATE_RANGE_OPTIONS.END_DATE
            ].format(LOST_ORDERS_DATE_FORMAT)
          })
        })
      );

      updateSearchOption('trackingNumbers')(
        updatedSearchOptions.trackingNumbers
      );

      return {
        list: formatData(data),
        total: getPaginationCount({
          page,
          result: data,
          pageSize: limit
        })
      };
    } catch (e) {
      notify(e.message);
    } finally {
      setLoading(false);
    }
  };

  const shareMethods = (refreshMethod) => {
    refreshTableRef.current = refreshMethod;
  };

  const getDeliveriesCount = async () => {
    setIsDeliveriesCountLoading(true);

    const { fetchCount } = functions[quickFilterValue];

    try {
      const res = await fetchCount({
        ...searchOptions,
        ...(quickFilterValue === '2' && {
          pendingOn: activeTab === 'hubs' ? 'HUB' : 'STAR'
        }),
        ...(selectedDateRange[LOST_ORDERS_DATE_RANGE_OPTIONS.START_DATE] && {
          startDate: selectedDateRange[
            LOST_ORDERS_DATE_RANGE_OPTIONS.START_DATE
          ].format(LOST_ORDERS_DATE_FORMAT)
        }),
        ...(selectedDateRange[LOST_ORDERS_DATE_RANGE_OPTIONS.END_DATE] && {
          endDate: selectedDateRange[
            LOST_ORDERS_DATE_RANGE_OPTIONS.END_DATE
          ].format(LOST_ORDERS_DATE_FORMAT)
        })
      });
      setDelieveriesCounts(res?.data);
      setShowDeliveriesCount(true);
    } catch (error) {
      notify(error.message);
    }

    setIsDeliveriesCountLoading(false);
  };

  const RADIO_GROUP_LABELS = [
    {
      label: intl.formatMessage({
        id: 'ops_control.lost_orders.pending_on_audit_tabs.from_hubs'
      }),
      value: 'hubs'
    },
    {
      label: intl.formatMessage({
        id: 'ops_control.lost_orders.pending_on_audit_tabs.from_fleet'
      }),
      value: 'fleet'
    }
  ];

  const handleDateChange = (dates, dateStrings) => {
    setSelectedDateRange(dates || [null, null]);
    refreshTableRef.current({
      filterValues: {
        ...(dates?.[0] && {
          startDate: dates[0].format(LOST_ORDERS_DATE_FORMAT)
        }),
        ...(dates?.[1] && {
          endDate: dates[1].format(LOST_ORDERS_DATE_FORMAT)
        })
      }
    });
  };

  return (
    <div className="br-lost-orders__container">
      <div className="br-lost-orders__header-wrapper">
        <PendingTransitFilter
          isHubFilterDisabled={!canViewAllHubs}
          loading={loading}
          intl={intl}
          allHubs={allHubs}
          currentHub={searchOptions?.warehouseId}
          setCurrentHub={updateSearchOption('warehouseId')}
          handleHubChange={handleHubChange}
          hubSelectionPlaceholder={intl.formatMessage({
            id: 'ops_control.wrong_zone.filters.hubs'
          })}
        />
        {quickFilterValue === '4' && (
          <div className="br-lost-orders__date-picker-container">
            <OpsRangeDatePicker
              selectedDateRange={selectedDateRange}
              setSelectedDateRange={setSelectedDateRange}
              onDateChange={handleDateChange}
              loading={loading}
            />
          </div>
        )}
      </div>
      <BRTable
        title={
          <div>
            <div className="ops_control__table-header title-sm">
              <DeliveriesIcon />
              <span className="ml-2">
                {showDeliveriesCount ? (
                  intl.formatMessage(
                    { id: 'ops_control.lost_orders.orders_count' },
                    { delieveriesCounts }
                  )
                ) : (
                  <BRButton
                    label={intl.formatMessage({
                      id: 'pickups.pickup_request.show_count'
                    })}
                    loading={isDeliveriesCountLoading}
                    prefixIcon={<ClosedEye />}
                    onClick={getDeliveriesCount}
                  />
                )}
              </span>
            </div>
          </div>
        }
        className="br-lost-orders__table"
        isLoading={loading}
        searchPlaceholder={intl.formatMessage({
          id: 'ops_control.lost_orders.search_place_holder'
        })}
        onSearchInputChange={updateSearchOption('trackingNumbers')}
        listFunction={listFunction}
        initialQuickFilter={quickFilterValue}
        handleQuickFilterChange={(value) => {
          setSelectedDateRange([null, null]);
          setQuickFilterValue(value);
        }}
        handleRadioGroupChange={(value) => {
          setActiveTab(value);
        }}
        initialActiveButton={activeTab}
        radioGroupLabels={
          quickFilterValue === '2' ? RADIO_GROUP_LABELS : undefined
        }
        columns={TABLE_COLUMNS[quickFilterValue]}
        pageSizeOptions={WRONG_ZONE_PAGE_OPTIONS}
        shareMethods={shareMethods}
        quickFilters={LOST_ORDERS_QUICK_FILTERS}
        hideFilterButton
        exportListFileFunction={exportFunction}
        showSearch
        keepDataBetweenTabs
      />
    </div>
  );
};

export default LostOrders;
